import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import Table from '../components/Table';
import Pagination from '../components/Pagination';
import StatsCard from '../components/StatsCard';
import toast from 'react-hot-toast';
import { CreditCardIcon, BanknotesIcon, ChartBarIcon, ArrowPathIcon, GlobeAltIcon, BuildingOfficeIcon, MagnifyingGlassIcon, ArrowDownTrayIcon, ChevronRightIcon, SparklesIcon } from '@heroicons/react/24/outline';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function formatMainPlanLabel(row) {
  if (row.is_bundle) {
    if (row.plan_type === 'pro') return 'Marketing Bundle';
    if (row.plan_type === 'ecommerce') return 'Ecommerce Bundle';
  }
  if (row.plan_type === 'pro') return 'Marketing';
  if (row.plan_type === 'landing_page') return 'Landing Page';
  return row.plan_type;
}

// Human-readable labels for the per-site add-on subscriptions surfaced in
// the "Add-ons" column. Keep in sync with FEATURE_LABELS on
// SubscriptionDetail.js — every new value of `feature_subscriptions.feature`
// should be added to BOTH so the list pill and the detail title agree.
// Falls back to a Title-Cased version of the raw key.
const ADDON_FEATURE_LABELS = {
  newsletter: 'Newsletter',
  crm: 'CRM',
  whatsapp_bot: 'WhatsApp Bot',
};
const formatAddonFeatureLabel = (feature) => {
  if (!feature) return '—';
  if (ADDON_FEATURE_LABELS[feature]) return ADDON_FEATURE_LABELS[feature];
  return feature.charAt(0).toUpperCase() + feature.slice(1);
};

const Subscriptions = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [exporting, setExporting] = useState(false);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: currentPage,
        limit: 20,
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(statusFilter && { status: statusFilter }),
        ...(searchQuery && { search: searchQuery })
      });

      const response = await axios.get(`${API_BASE_URL}/api/admin/subscriptions?${params}`);
      setSubscriptions(response.data.data.subscriptions);
      setTotalPages(response.data.data.pagination.totalPages);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to fetch subscriptions');
    } finally {
      setLoading(false);
    }
  };

  // Debounced search handler
  const handleSearch = useCallback(() => {
    setSearchQuery(searchInput);
    setCurrentPage(1);
  }, [searchInput]);

  // Handle search on Enter key
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // Export to CSV
  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams({
        sort_by: sortBy,
        sort_order: sortOrder,
        ...(statusFilter && { status: statusFilter }),
        ...(searchQuery && { search: searchQuery })
      });

      const response = await axios.get(`${API_BASE_URL}/api/admin/subscriptions/export/csv?${params}`, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `subscriptions_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/subscriptions/stats/revenue`);
      setStats(response.data.data);
    } catch (error) {
      console.error('Error fetching subscription stats:', error);
    }
  };

  useEffect(() => {
    fetchSubscriptions();
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, statusFilter, searchQuery, sortBy, sortOrder]);

  const handleSort = (field, order) => {
    setSortBy(field);
    setSortOrder(order);
    setCurrentPage(1);
  };

  const columns = [
    { 
      key: 'account_name', 
      label: 'Account',
      render: (row) => (
        <div>
          <Link 
            to={`/subscriptions/${row.id}`}
            className="font-medium text-primary-600 hover:text-primary-800 flex items-center gap-1"
          >
            <BuildingOfficeIcon className="w-4 h-4" />
            {row.account_name || 'Unknown Account'}
          </Link>
          <p className="text-xs text-gray-500">{row.user_name} • {row.user_email}</p>
        </div>
      )
    },
    { 
      key: 'website_name', 
      label: 'Website',
      render: (row) => (
        <div>
          {row.website_id ? (
            <>
              <p className="font-medium flex items-center gap-1">
                <GlobeAltIcon className="w-4 h-4 text-gray-400" />
                {row.website_name || 'Unnamed Site'}
              </p>
              {row.website_domain && (
                <p className="text-xs text-gray-500">{row.website_domain}</p>
              )}
            </>
          ) : (
            <span className="text-gray-400 italic text-sm">Legacy (account-wide)</span>
          )}
        </div>
      )
    },
    {
      key: 'purchased_domain',
      label: 'Domain',
      render: (row) => row.purchased_domain ? (
        <div>
          <span className="text-sm font-medium text-gray-900">{row.purchased_domain}</span>
          <span className={`ml-1.5 px-1.5 py-0.5 text-[10px] font-semibold rounded-full ${
            row.domain_purchase_status === 'active' ? 'bg-green-100 text-green-700' :
            row.domain_purchase_status === 'failed' ? 'bg-red-100 text-red-700' :
            'bg-yellow-100 text-yellow-700'
          }`}>
            {row.domain_purchase_status}
          </span>
        </div>
      ) : (
        <span className="text-gray-300">—</span>
      )
    },
    { 
      key: 'plan_type', 
      label: 'Plan',
      render: (row) => (
        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
          row.plan_type === 'pro' ? 'bg-blue-100 text-blue-800' :
          row.plan_type === 'ecommerce' ? 'bg-purple-100 text-purple-800' :
          row.plan_type === 'landing_page' ? 'bg-green-100 text-green-700' :
          'bg-gray-100 text-gray-800'
        }`}>
          {formatMainPlanLabel(row)}
        </span>
      )
    },
    {
      key: 'feature_subscriptions',
      label: 'Add-ons',
      render: (row) => {
        const addons = Array.isArray(row.feature_subscriptions) ? row.feature_subscriptions : [];
        if (addons.length === 0) return <span className="text-gray-300">—</span>;
        return (
          <div className="flex flex-wrap gap-1">
            {addons.map((addon) => {
              const symbol = addon.currency === 'ILS' ? '₪' : '$';
              const amount = addon.amount ? parseFloat(addon.amount).toFixed(0) : '—';
              const label = formatAddonFeatureLabel(addon.feature);
              const isCanceling = addon.status === 'active' && addon.cancel_at_period_end;
              const tone =
                isCanceling ? 'bg-orange-100 text-orange-800' :
                addon.status === 'active' ? 'bg-pink-100 text-pink-800' :
                addon.status === 'past_due' ? 'bg-yellow-100 text-yellow-800' :
                'bg-gray-100 text-gray-700';
              const titleParts = [
                `Status: ${isCanceling ? 'canceling' : addon.status}`,
                `${symbol}${amount} ${addon.currency || ''} / ${addon.billing_period || 'monthly'}`,
                addon.payment_provider ? `Paid via ${addon.payment_provider === 'paypal' ? 'PayPal' : 'iCount'}` : null
              ].filter(Boolean);
              return (
                <span
                  key={addon.id}
                  title={titleParts.join(' • ')}
                  className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${tone}`}
                >
                  {label}
                </span>
              );
            })}
          </div>
        );
      }
    },
    { 
      key: 'billing_period', 
      label: 'Billing',
      render: (row) => (
        <span className="capitalize text-sm">{row.billing_period}</span>
      )
    },
    { 
      key: 'status', 
      label: 'Status',
      render: (row) => {
        const isCanceling = row.status === 'active' && row.cancel_at_period_end;
        // Server-side marker: subs canceled by /cancel-for-upgrade get
        // cancellation_reason='plan_upgrade'. Surfacing it here lets ops
        // distinguish retained-revenue upgrades from real churn at-a-glance
        // in the Subscriptions list, without opening each row.
        const isPlanUpgrade = row.status === 'canceled' && row.cancellation_reason === 'plan_upgrade';
        
        if (isCanceling) {
          return (
            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
              canceling
            </span>
          );
        }
        
        return (
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
              row.status === 'active' ? 'bg-green-100 text-green-800' :
              row.status === 'past_due' ? 'bg-yellow-100 text-yellow-800' :
              row.status === 'canceled' ? 'bg-red-100 text-red-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {row.status}
            </span>
            {isPlanUpgrade && (
              <span
                className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800"
                title="Canceled because the customer upgraded to a higher plan"
              >
                upgraded
              </span>
            )}
          </div>
        );
      }
    },
    { 
      key: 'amount', 
      label: 'Amount',
      render: (row) => {
        const currencySymbol = row.currency === 'ILS' ? '₪' : '$';
        return (
          <span className="font-medium">{currencySymbol}{parseFloat(row.amount).toFixed(2)}</span>
        );
      }
    },
    {
      key: 'payment_provider',
      label: 'Payment',
      render: (row) => {
        const isPaypal = row.payment_provider === 'paypal';
        return (
          <div>
            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
              isPaypal ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'
            }`}>
              {isPaypal ? 'PayPal' : 'Credit Card'}
            </span>
            <p className="text-xs font-mono text-gray-400 mt-1 truncate max-w-[120px]" title={isPaypal ? row.paypal_subscription_id : row.icount_subscription_id}>
              {(isPaypal ? row.paypal_subscription_id : row.icount_subscription_id) || '—'}
            </p>
          </div>
        );
      }
    },
    {
      key: 'current_period_end',
      label: 'Period End',
      render: (row) => (
        <span className="text-sm">{row.current_period_end ? new Date(row.current_period_end).toLocaleDateString() : 'N/A'}</span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (row) => (
        <span className="text-sm text-gray-500">{row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A'}</span>
      )
    },
    {
      key: '_link',
      label: '',
      render: (row) => (
        <Link to={`/subscriptions/${row.id}`} className="text-gray-400 hover:text-primary-600">
          <ChevronRightIcon className="w-5 h-5" />
        </Link>
      )
    }
  ];

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Subscriptions Management</h1>
          <button
            onClick={() => { fetchSubscriptions(); fetchStats(); }}
            disabled={loading}
            className="p-2 text-gray-600 hover:text-primary-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh data"
          >
            <ArrowPathIcon className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5 mb-8">
            <StatsCard
              title="Paying Plans"
              value={stats.activeSubscriptions}
              icon={CreditCardIcon}
              subtitle={
                typeof stats.activeEngagementBundles === 'number'
                  ? `${stats.activeSitePlans ?? 0} site · ${stats.activeEngagementBundles} engagement`
                  : 'Site + engagement'
              }
            />
            <StatsCard
              title="Engagement Bundles"
              value={stats.activeEngagementBundles ?? 0}
              icon={SparklesIcon}
              subtitle="Growth / Business / Scale"
            />
            <StatsCard
              title="Sites with Site Plans"
              value={stats.sitesWithSubscriptions || stats.activeSitePlans || 0}
              icon={GlobeAltIcon}
              subtitle="Legacy per-site billing"
            />
            <StatsCard
              title="Total Revenue (USD)"
              value={`$${stats.totalRevenue.toFixed(2)}`}
              icon={BanknotesIcon}
              subtitle="Site plans + engagement"
            />
            <StatsCard
              title="Monthly Recurring (USD)"
              value={`$${stats.monthlyRecurringRevenue.toFixed(2)}`}
              icon={ChartBarIcon}
              subtitle={
                typeof stats.engagementMrr === 'number'
                  ? `MRR · $${(stats.engagementMrr || 0).toFixed(0)} engagement`
                  : 'MRR'
              }
            />
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center flex-1">
              {/* Search */}
              <div className="relative flex-1 w-full sm:max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by account, email, website..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  className="input-field pl-10 w-full"
                />
              </div>
              <button
                onClick={handleSearch}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors whitespace-nowrap"
              >
                Search
              </button>
              
              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="input-field w-full sm:w-48"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="canceling">Canceling</option>
                <option value="past_due">Past Due</option>
                <option value="canceled">Canceled</option>
                <option value="suspended">Suspended</option>
              </select>
            </div>

            {/* Export Button */}
            <button
              onClick={handleExportCSV}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 whitespace-nowrap"
            >
              <ArrowDownTrayIcon className={`w-5 h-5 ${exporting ? 'animate-bounce' : ''}`} />
              {exporting ? 'Exporting...' : 'Export CSV'}
            </button>
          </div>

          {/* Active search indicator */}
          {searchQuery && (
            <div className="mt-3 flex items-center gap-2 text-sm text-gray-600">
              <span>Showing results for:</span>
              <span className="px-2 py-1 bg-primary-100 text-primary-700 rounded-full font-medium">
                {searchQuery}
              </span>
              <button
                onClick={() => {
                  setSearchInput('');
                  setSearchQuery('');
                  setCurrentPage(1);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                Clear
              </button>
            </div>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <Table
            columns={columns}
            data={subscriptions}
            loading={loading}
            emptyMessage="No subscriptions found"
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default Subscriptions;
